/**
 * Emission Factor Management Page
 *
 * Admin-only page for viewing and managing emission factors.
 * Allows viewing, creating, editing, and deleting emission factors.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Science as ScienceIcon,
  Search as SearchIcon,
  LocalFireDepartment as Scope1Icon,
  Bolt as Scope2Icon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalGasStation as FuelIcon,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import {
  emissionFactorService,
  EmissionFactor,
} from '../../services/emissionFactorService';

// Scope options for filter (only Scope 1 and 2)
const SCOPES = [
  { value: 0, label: 'All (Scope 1 & 2)' },
  { value: 1, label: 'Scope 1 - Direct' },
  { value: 2, label: 'Scope 2 - Energy' },
];

// Category options (Scope 1 & 2 only)
const CATEGORIES = [
  { value: '', label: 'All Categories' },
  // Scope 1 categories
  { value: 'stationary_combustion', label: 'Stationary Combustion' },
  { value: 'mobile_combustion', label: 'Mobile Combustion' },
  { value: 'fugitive_emissions', label: 'Fugitive Emissions' },
  { value: 'process_emissions', label: 'Process Emissions' },
  // Scope 2 categories
  { value: 'purchased_electricity', label: 'Purchased Electricity' },
  { value: 'electricity', label: 'Electricity (Grid)' },
  { value: 'heat_steam', label: 'Heat & Steam' },
];

// Empty factor for creating new
const EMPTY_FACTOR: Partial<EmissionFactor> = {
  source: 'Custom',
  scope: 1,
  category: 'stationary_combustion',
  activity: '',
  fuel_type: '',
  value: 0,
  unit_input: 'kWh',
  unit_output: 'kg CO2e',
  region: 'UK',
};

export const EmissionFactorManagement: React.FC = () => {
  const { user } = useApp();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importingEpa, setImportingEpa] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Factors data
  const [factors, setFactors] = useState<EmissionFactor[]>([]);
  const [totalFactors, setTotalFactors] = useState(0);

  // Filters
  const [scopeFilter, setScopeFilter] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFactor, setEditingFactor] = useState<Partial<EmissionFactor> | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [factorToDelete, setFactorToDelete] = useState<EmissionFactor | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  // Load factors
  const loadFactors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await emissionFactorService.listFactors({
        scope: scopeFilter > 0 ? scopeFilter : undefined,
        category: categoryFilter || undefined,
        page: page + 1,
        page_size: rowsPerPage,
      });
      setFactors(response.factors);
      setTotalFactors(response.total);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load factors:', err);
      setError(err.message || 'Failed to load emission factors');
    } finally {
      setLoading(false);
    }
  }, [scopeFilter, categoryFilter, page, rowsPerPage]);

  useEffect(() => {
    if (isAdmin) {
      loadFactors();
    }
  }, [isAdmin, loadFactors]);

  // Clear alerts after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Handle import DEFRA factors
  const handleImportDefra = async () => {
    try {
      setImporting(true);
      const result = await emissionFactorService.importDefraFactors();
      setSuccess(`Successfully imported ${result.total_imported || 0} emission factors from DEFRA`);
      await loadFactors();
    } catch (err: any) {
      console.error('Import failed:', err);
      setError(err.message || 'Failed to import DEFRA factors');
    } finally {
      setImporting(false);
    }
  };

  // Handle import EPA Mobile Combustion factors
  const handleImportEpaMobile = async () => {
    try {
      setImportingEpa(true);
      const result = await emissionFactorService.importEpaMobileFactors();
      setSuccess(`Successfully imported ${result.total_imported || 0} EPA mobile combustion factors`);
      await loadFactors();
    } catch (err: any) {
      console.error('EPA import failed:', err);
      setError(err.message || 'Failed to import EPA mobile combustion factors');
    } finally {
      setImportingEpa(false);
    }
  };

  // Handle filter changes
  const handleScopeChange = (value: number) => {
    setScopeFilter(value);
    setPage(0);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setPage(0);
  };

  // Filter factors by search text (client-side for displayed page)
  const filteredFactors = searchText
    ? factors.filter(
        (f) =>
          f.fuel_type?.toLowerCase().includes(searchText.toLowerCase()) ||
          f.activity?.toLowerCase().includes(searchText.toLowerCase()) ||
          f.category?.toLowerCase().includes(searchText.toLowerCase()) ||
          f.refrigerant?.toLowerCase().includes(searchText.toLowerCase()) ||
          f.vehicle_type?.toLowerCase().includes(searchText.toLowerCase())
      )
    : factors;

  // Open create dialog
  const handleOpenCreate = () => {
    setEditingFactor({ ...EMPTY_FACTOR });
    setIsEditing(false);
    setDialogOpen(true);
  };

  // Open edit dialog
  const handleOpenEdit = (factor: EmissionFactor) => {
    setEditingFactor({ ...factor });
    setIsEditing(true);
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingFactor(null);
    setIsEditing(false);
  };

  // Handle save (create or update)
  const handleSave = async () => {
    if (!editingFactor) return;

    try {
      setSaving(true);
      if (isEditing && editingFactor.id) {
        await emissionFactorService.updateFactor(editingFactor.id, editingFactor);
        setSuccess('Emission factor updated successfully');
      } else {
        await emissionFactorService.createCustomFactor(editingFactor);
        setSuccess('Emission factor created successfully');
      }
      handleCloseDialog();
      await loadFactors();
    } catch (err: any) {
      console.error('Save failed:', err);
      setError(err.message || 'Failed to save emission factor');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete click
  const handleDeleteClick = (factor: EmissionFactor) => {
    setFactorToDelete(factor);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!factorToDelete) return;

    try {
      setDeleting(true);
      await emissionFactorService.deleteFactor(factorToDelete.id);
      setSuccess('Emission factor deleted successfully');
      setDeleteDialogOpen(false);
      setFactorToDelete(null);
      await loadFactors();
    } catch (err: any) {
      console.error('Delete failed:', err);
      setError(err.message || 'Failed to delete emission factor');
    } finally {
      setDeleting(false);
    }
  };

  // Update editing factor field
  const updateField = (field: keyof EmissionFactor, value: any) => {
    if (!editingFactor) return;
    setEditingFactor({ ...editingFactor, [field]: value });
  };

  // Get scope icon
  const getScopeIcon = (scope: number) => {
    switch (scope) {
      case 1:
        return <Scope1Icon fontSize="small" />;
      case 2:
        return <Scope2Icon fontSize="small" />;
      default:
        return null;
    }
  };

  // Get scope color
  const getScopeColor = (scope: number): 'error' | 'warning' | 'default' => {
    switch (scope) {
      case 1:
        return 'error';
      case 2:
        return 'warning';
      default:
        return 'default';
    }
  };

  // Format category for display
  const formatCategory = (category: string): string => {
    return category
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Access denied check
  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access Denied. Only administrators can manage emission factors.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Emission Factors
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage emission factors (DEFRA for stationary/fugitive, EPA for mobile combustion)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadFactors}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <ScienceIcon />}
            onClick={handleImportDefra}
            disabled={importing}
            size="small"
          >
            {importing ? 'Importing...' : 'Import DEFRA'}
          </Button>
          <Button
            variant="outlined"
            startIcon={importingEpa ? <CircularProgress size={16} /> : <FuelIcon />}
            onClick={handleImportEpaMobile}
            disabled={importingEpa}
            size="small"
            color="warning"
          >
            {importingEpa ? 'Importing...' : 'Import EPA Mobile'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
            size="small"
          >
            Add Factor
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScienceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {loading ? '-' : totalFactors.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Factors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Scope1Icon sx={{ fontSize: 40, color: 'error.main' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Scope 1
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Direct Emissions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Scope2Icon sx={{ fontSize: 40, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Scope 2
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Energy Indirect
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Scope"
              value={scopeFilter}
              onChange={(e) => handleScopeChange(Number(e.target.value))}
            >
              {SCOPES.map((scope) => (
                <MenuItem key={scope.value} value={scope.value}>
                  {scope.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Category"
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.target.value)}
            >
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by fuel type, refrigerant, activity..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Factors Table */}
      <Paper>
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading emission factors...
            </Typography>
          </Box>
        ) : filteredFactors.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No emission factors found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {totalFactors === 0
                ? 'Click "Import DEFRA" to load emission factors or "Add Factor" to create a custom one.'
                : 'Try adjusting your filters.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Scope</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Fuel/Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Value</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFactors.map((factor) => (
                    <TableRow key={factor.id} hover>
                      <TableCell>
                        <Chip
                          icon={getScopeIcon(factor.scope) || undefined}
                          label={`Scope ${factor.scope}`}
                          size="small"
                          color={getScopeColor(factor.scope)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatCategory(factor.category)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatCategory(factor.activity || '-')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={factor.fuel_type || factor.vehicle_type || factor.refrigerant || '-'}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 150,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {factor.fuel_type || factor.vehicle_type || factor.refrigerant || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {factor.value.toFixed(6)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {factor.unit_input} → {factor.unit_output}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={factor.source}
                          size="small"
                          color={factor.is_custom ? 'warning' : 'primary'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(factor)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(factor)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalFactors}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEditing ? 'Edit Emission Factor' : 'Create New Emission Factor'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Row 1: Scope, Source */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Scope"
                value={editingFactor?.scope || 1}
                onChange={(e) => updateField('scope', Number(e.target.value))}
              >
                <MenuItem value={1}>Scope 1 - Direct Emissions</MenuItem>
                <MenuItem value={2}>Scope 2 - Energy Indirect</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Source"
                value={editingFactor?.source || 'Custom'}
                onChange={(e) => updateField('source', e.target.value)}
                helperText="e.g., DEFRA, EPA, Custom"
              />
            </Grid>

            {/* Row 2: Category, Activity */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Category"
                value={editingFactor?.category || ''}
                onChange={(e) => updateField('category', e.target.value)}
              >
                {CATEGORIES.filter(c => c.value).map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Activity"
                value={editingFactor?.activity || ''}
                onChange={(e) => updateField('activity', e.target.value)}
                helperText="e.g., gaseous_fuels, liquid_fuels"
              />
            </Grid>

            {/* Row 3: Fuel Type, Vehicle Type */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Fuel Type"
                value={editingFactor?.fuel_type || ''}
                onChange={(e) => updateField('fuel_type', e.target.value)}
                helperText="e.g., Natural Gas, Diesel"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Vehicle Type"
                value={editingFactor?.vehicle_type || ''}
                onChange={(e) => updateField('vehicle_type', e.target.value)}
                helperText="For mobile combustion"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Refrigerant"
                value={editingFactor?.refrigerant || ''}
                onChange={(e) => updateField('refrigerant', e.target.value)}
                helperText="For fugitive emissions (e.g., R-134a, HFC-23)"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Emission Values
              </Typography>
            </Grid>

            {/* Row 4: Value, Unit Input, Unit Output */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Emission Factor Value"
                value={editingFactor?.value || 0}
                onChange={(e) => updateField('value', parseFloat(e.target.value) || 0)}
                inputProps={{ step: '0.000001' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Input Unit"
                value={editingFactor?.unit_input || 'kWh'}
                onChange={(e) => updateField('unit_input', e.target.value)}
                helperText="e.g., kWh, litres, tonnes"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Output Unit"
                value={editingFactor?.unit_output || 'kg CO2e'}
                onChange={(e) => updateField('unit_output', e.target.value)}
              />
            </Grid>

            {/* Row 5: CO2, CH4, N2O values */}
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="CO2 Value (optional)"
                value={editingFactor?.co2_value || ''}
                onChange={(e) => updateField('co2_value', e.target.value ? parseFloat(e.target.value) : undefined)}
                inputProps={{ step: '0.000001' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="CH4 Value (optional)"
                value={editingFactor?.ch4_value || ''}
                onChange={(e) => updateField('ch4_value', e.target.value ? parseFloat(e.target.value) : undefined)}
                inputProps={{ step: '0.000001' }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="N2O Value (optional)"
                value={editingFactor?.n2o_value || ''}
                onChange={(e) => updateField('n2o_value', e.target.value ? parseFloat(e.target.value) : undefined)}
                inputProps={{ step: '0.000001' }}
              />
            </Grid>

            {/* Row 6: Region */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Region"
                value={editingFactor?.region || 'UK'}
                onChange={(e) => updateField('region', e.target.value)}
                helperText="e.g., UK, US, Global"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editingFactor?.category || !editingFactor?.value}
            startIcon={saving ? <CircularProgress size={20} /> : undefined}
          >
            {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Emission Factor</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone!
          </Alert>
          <Typography>
            Are you sure you want to delete this emission factor?
          </Typography>
          {factorToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Category:</strong> {formatCategory(factorToDelete.category)}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {factorToDelete.fuel_type || factorToDelete.vehicle_type || factorToDelete.refrigerant || '-'}
              </Typography>
              <Typography variant="body2">
                <strong>Value:</strong> {factorToDelete.value} {factorToDelete.unit_input} → {factorToDelete.unit_output}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmissionFactorManagement;
